import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LayoutSettings } from "../../../../boxes/LayoutSettings/LayoutSettings";
import { EntityHeader } from "../../../../widgets/EntityHeader/EntityHeader";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";
import { Toggle } from "../../../../shared/Toggle/Toggle";
import styles from "./scss/LeadArIntegration.module.scss";
import { Box } from "../../../../shared/Box/Box";
import LeadArIntegrationTable from "./components/LeadArIntegrationTable";
import leadArLogo from "../../../../assets/img/leadar-logo.png";
import { useLeadArConfig } from "./hooks/useLeadArConfig";
import { useSaveLeadArConfig } from "./hooks/useSaveLeadArConfig";
import { LEAD_AR_INTEGRATION_LINK } from "./LeadArIntegration.utils";
import { QUERY_KEYS } from "../../../../consts/api";
import { getIsAcceleratorPlan } from "../../../../Utils";
import UpgradeAccountModal from "../../../../Components/Common/UpgradeAccountModal";

const LeadArIntegration = () => {
  const { tSettings } = useAppTranslation.Settings();
  const [isIntegrationEnabled, setIsIntegrationEnabled] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(
    !getIsAcceleratorPlan(),
  );
  const queryClient = useQueryClient();

  const { data, isFetching } = useLeadArConfig({
    enabled: getIsAcceleratorPlan(),
    onSuccess: (data) => setIsIntegrationEnabled(data.isEnabled),
  });
  const { mutate: saveLeadArConfig, isLoading: isMutating } =
    useSaveLeadArConfig({
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEYS.leadArIntegration]);
      },
    });

  const isIntegrationLoading = isFetching || isMutating;

  return (
    <LayoutSettings contentClassName={styles.root}>
      <EntityHeader
        title={tSettings("leadArIntegration.title")}
        right={
          <div className={!getIsAcceleratorPlan() && "cursor-not-allowed"}>
            {isIntegrationLoading ? (
              <CircularProgress size="small" />
            ) : (
              <Toggle
                isEnabled={isIntegrationEnabled}
                onChange={() => {
                  saveLeadArConfig({
                    isEnabled: !isIntegrationEnabled,
                    locationId: data?.locationId || "",
                  });
                }}
                isDisabled={isIntegrationLoading || !getIsAcceleratorPlan()}
              />
            )}
          </div>
        }
      />
      {isIntegrationEnabled && (
        <>
          <Box className={styles.mainContainer}>
            <div className={styles.startPlanContainer}>
              <h3>START Plan Activation</h3>
              <img src={leadArLogo} alt="leadAr logo" />
            </div>
            <p>
              Connect your Aesthetic Record account to our natively integrated
              CRM & Marketing platform, LeadAR, for expanded marketing and
              client relationship functionality.
            </p>
            <p className={styles.price}>$97 / per Month</p>
            <p>
              This plan is only available to Aesthetic Record Clients and
              includes the following LeadAR features:
            </p>
            <ul className={styles.featuresList}>
              <li>Client & lead sync between Aesthetic Record & LeadAR</li>
              <li>SMS & Email campaign functionality</li>
              <li>SMS & Email snippets and templates</li>
              <li>Inbound & Outbound VOIP & app-based calling</li>
              <li>LEADAR App (calls, messages)</li>
              <li>Contact management (tagging, notes, custom fields, etc)</li>
              <li>Social Media Management</li>
              <li className={styles.subFeaturesList}>
                <ul>
                  <li>Meta integration</li>
                  <li>Linkedin Integration</li>
                  <li>Social Planner</li>
                  <li>Performance Metrics</li>
                </ul>
              </li>
            </ul>
            <p className={styles.footerNote}>
              Click below to complete the integration. Monthly recurring
              payments, SMS and Email usage will be billed separately from
              Aesthetic Record. Once payment is confirmed, you will receive
              directions to complete A2P-10DLC registration and Email DNS setup.
              Please note, until A2P-10DLC registration is approved via Twilio,
              you will be unable to send SMS via LeadAR.
            </p>
            <a
              className={styles.link}
              href={LEAD_AR_INTEGRATION_LINK}
              target="_blank"
              rel="noopener noreferrer"
            >
              Complete Enrollment
            </a>
          </Box>

          <Box className={styles.accountInfoContainer}>
            <h3>LeadAR Account Information</h3>
            <div>
              <LeadArIntegrationTable
                locationId={data?.locationId}
                saveLeadArConfig={saveLeadArConfig}
                isMutating={isMutating}
              />
            </div>
          </Box>
        </>
      )}
      {!getIsAcceleratorPlan() && (
        <UpgradeAccountModal
          notificaionType={"integration"}
          isShowUpgradeAlertModal={showUpgradeModal}
          handleShowUpgradeAlertModal={() =>
            setShowUpgradeModal(!showUpgradeModal)
          }
        />
      )}
    </LayoutSettings>
  );
};

export default LeadArIntegration;
